import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { auth } from '../../firebaseConfig';
import { database } from '../../firebaseConfig';
import { ref, get } from 'firebase/database';
import './BusinessAnalysisCompare.css';

function BusinessAnalysisCompare() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [analyses, setAnalyses] = useState([]);
    const [allAnalyses, setAllAnalyses] = useState([]);
    const [isLoadingComparison, setIsLoadingComparison] = useState(false);
    const [isLoadingList, setIsLoadingList] = useState(true);
    const [selectedIds, setSelectedIds] = useState([]);

    useEffect(() => {
        const ids = searchParams.get('ids');
        if (ids) {
            setSelectedIds(ids.split(','));
        }
    }, [searchParams]);

    useEffect(() => {
        loadAllAnalyses();
    }, []);

    useEffect(() => {
        if (selectedIds.length > 0) {
            loadSelectedAnalyses();
        } else {
            setAnalyses([]);
        }
    }, [selectedIds]);

    const loadAllAnalyses = async () => {
        if (!auth.currentUser) return;
        
        setIsLoadingList(true);
        try {
            const userId = auth.currentUser.uid;
            const analysesRef = ref(database, `businessAnalyses/${userId}`);
            const snapshot = await get(analysesRef);
            
            if (snapshot.exists()) {
                const data = snapshot.val();
                const analysesArray = Object.keys(data).map(key => ({
                    id: key,
                    name: data[key].name || 'Unbenannte Analyse',
                    ...data[key]
                }));
                setAllAnalyses(analysesArray);
            }
        } catch (error) {
            console.error('Error loading analyses:', error);
        }
        setIsLoadingList(false);
    };

    const loadSelectedAnalyses = async () => {
        if (!auth.currentUser || selectedIds.length === 0) return;
        
        setIsLoadingComparison(true);
        try {
            const userId = auth.currentUser.uid;
            const loadedAnalyses = [];
            
            for (const id of selectedIds) {
                const analysisRef = ref(database, `businessAnalyses/${userId}/${id}`);
                const snapshot = await get(analysisRef);
                
                if (snapshot.exists()) {
                    const data = snapshot.val();
                    const analysis = calculateBusinessStrength(data);
                    loadedAnalyses.push({
                        id,
                        data,
                        analysis
                    });
                }
            }
            
            setAnalyses(loadedAnalyses);
        } catch (error) {
            console.error('Error loading selected analyses:', error);
        }
        setIsLoadingComparison(false);
    };

    const calculateBusinessStrength = (analysisData) => {
        if (!analysisData) return null;

        // Branding Score
        const branding = analysisData.branding || {};
        const brandingScore = ((branding.brandingQuality || 5) + 
                              (branding.messageDesignAlignment || 5) + 
                              (branding.photoQuality || 5) + 
                              (branding.videoMaterial || 5)) / 4 * 10;
        
        // Lead Sources Score
        const leadCount = analysisData.leadSources?.length || 0;
        const leadAvg = leadCount > 0 
            ? analysisData.leadSources.reduce((sum, s) => sum + (s.strength || 5), 0) / leadCount 
            : 5;
        const leadScore = Math.min((leadCount * 15) + (leadAvg * 5), 100);
        
        // Product Portfolio Score
        const productCount = analysisData.products?.filter(p => p.name && p.name.trim().length > 0).length || 0;
        const hasHighTicket = analysisData.products?.some(p => ['500-1000', '1000-5000', '5000-10000', '10000-20000', '20000-50000', 'over50000'].includes(p.cost)) || false;
        const hasRecurring = analysisData.products?.some(p => p.paymentType === 'monthly') || false;
        const hasRevolutionary = analysisData.products?.some(p => p.exclusivity === 'revolutionary') || false;
        const productScore = Math.min((productCount * 15) + (hasHighTicket ? 25 : 0) + (hasRecurring ? 30 : 0) + (hasRevolutionary ? 15 : 0), 100);
        
        // Social Proof Score
        const proofCount = analysisData.socialProof?.length || 0;
        const proofAvg = proofCount > 0 
            ? analysisData.socialProof.reduce((sum, p) => sum + (p.strength || 5), 0) / proofCount 
            : 5;
        const proofScore = Math.min((proofCount * 12) + (proofAvg * 6), 100);
        
        // Overall Score
        const overallScore = Math.round(
            (brandingScore * 0.30) +
            (leadScore * 0.30) +
            (productScore * 0.20) +
            (proofScore * 0.20)
        );
        
        // Calculate total followers from lead sources
        const totalFollowers = analysisData.leadSources?.reduce((sum, source) => {
            const count = parseInt(source.followerCount) || 0;
            return sum + count;
        }, 0) || 0;

        const socialProofCount = analysisData.socialProof?.length || 0;
        
        return {
            name: analysisData.name || 'Unbenannt',
            overallScore,
            brandingScore: Math.round(brandingScore),
            leadScore: Math.round(leadScore),
            productScore: Math.round(productScore),
            proofScore: Math.round(proofScore),
            leadCount,
            productCount,
            hasRecurring,
            hasHighTicket,
            hasRevolutionary,
            totalFollowers,
            socialProofCount
        };
    };

    const handleSelectAnalysis = (id) => {
        let newSelectedIds = [...selectedIds];
        if (newSelectedIds.includes(id)) {
            newSelectedIds = newSelectedIds.filter(i => i !== id);
        } else {
            if (newSelectedIds.length < 4) {
                newSelectedIds.push(id);
            } else {
                alert('Maximal 4 Analysen können gleichzeitig verglichen werden.');
                return;
            }
        }
        setSelectedIds(newSelectedIds);
        
        if (newSelectedIds.length > 0) {
            navigate(`/business-analysis/compare?ids=${newSelectedIds.join(',')}`);
        } else {
            navigate('/business-analysis/compare');
        }
    };

    const formatNumber = (num) => {
        if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
        if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
        return num?.toString() || '0';
    };

    const getScoreColor = (score) => {
        if (score >= 80) return '#00ff88';
        if (score >= 65) return '#e91e63';
        if (score >= 50) return '#ffd93d';
        return '#ff6b9d';
    };

    const getBestPerformer = (category) => {
        if (analyses.length === 0) return null;
        
        let bestScore = 0;
        let bestIndex = 0;
        
        analyses.forEach((item, index) => {
            const score = item.analysis[category];
            if (score > bestScore) {
                bestScore = score;
                bestIndex = index;
            }
        });
        
        return bestIndex;
    };

    return (
        <div className="business-compare">
            {/* Header */}
            <div className="business-compare-header">
                <div className="business-compare-header-content">
                    <button 
                        className="business-compare-back-btn"
                        onClick={() => navigate('/business-analysis')}
                    >
                        <i className="fas fa-arrow-left"></i>
                    </button>
                    <div>
                        <h1 className="business-compare-title">Business-Analysen Vergleichen</h1>
                        <p className="business-compare-subtitle">
                            Vergleichen Sie bis zu 4 Analysen nebeneinander
                        </p>
                    </div>
                </div>
            </div>

            <div className="business-compare-container">
                {/* Selection Area */}
                <div className="business-compare-selection">
                    <h3><i className="fas fa-check-square"></i> Analysen auswählen ({selectedIds.length}/4)</h3>
                    {isLoadingList ? (
                        <div className="business-compare-selection-loading">
                            <div className="business-compare-spinner"></div>
                            <p>Lade Analysen...</p>
                        </div>
                    ) : allAnalyses.length === 0 ? (
                        <div className="business-compare-no-analyses">
                            <i className="fas fa-inbox"></i>
                            <p>Keine Analysen gefunden. Erstelle zuerst eine Analyse.</p>
                            <button 
                                className="business-compare-create-btn"
                                onClick={() => navigate('/business-analysis/add')}
                            >
                                <i className="fas fa-plus"></i>
                                Analyse erstellen
                            </button>
                        </div>
                    ) : (
                        <div className="business-compare-selection-grid">
                            {allAnalyses.map(analysis => (
                            <div
                                key={analysis.id}
                                className={`business-compare-selection-card ${selectedIds.includes(analysis.id) ? 'selected' : ''}`}
                                onClick={() => handleSelectAnalysis(analysis.id)}
                            >
                                <div className="business-compare-selection-checkbox">
                                    {selectedIds.includes(analysis.id) && <i className="fas fa-check"></i>}
                                </div>
                                <div className="business-compare-selection-info">
                                    <h4>{analysis.name}</h4>
                                    <span className="business-compare-selection-meta">
                                        {analysis.brandType === 'personal' ? 'Personal Brand' : 'Corporate Brand'}
                                    </span>
                                </div>
                            </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Loading indicator for comparison */}
                {isLoadingComparison && selectedIds.length > 0 && (
                    <div className="business-compare-loading-indicator">
                        <div className="business-compare-spinner"></div>
                        <p>Bereite Vergleich vor...</p>
                    </div>
                )}

                {/* Comparison View */}
                {!isLoadingComparison && analyses.length >= 2 ? (
                    <>
                        {/* Overall Scores */}
                        <div className="business-compare-section">
                            <h2><i className="fas fa-chart-bar"></i> Gesamt-Scores im Vergleich</h2>
                            <div className="business-compare-scores-grid">
                                {analyses.map((item, index) => {
                                    const isBest = getBestPerformer('overallScore') === index;
                                    return (
                                        <div key={item.id} className={`business-compare-score-card ${isBest ? 'best' : ''}`}>
                                            {isBest && <div className="business-compare-best-badge">🏆 Beste</div>}
                                            <h3>{item.analysis.name}</h3>
                                            <div className="business-compare-score-circle">
                                                <svg width="120" height="120" viewBox="0 0 120 120">
                                                    <circle cx="60" cy="60" r="50" fill="none" stroke="rgba(212, 195, 180, 0.3)" strokeWidth="10" />
                                                    <circle
                                                        cx="60"
                                                        cy="60"
                                                        r="50"
                                                        fill="none"
                                                        stroke={getScoreColor(item.analysis.overallScore)}
                                                        strokeWidth="10"
                                                        strokeDasharray={`${(item.analysis.overallScore / 100) * 314} 314`}
                                                        strokeLinecap="round"
                                                        transform="rotate(-90 60 60)"
                                                    />
                                                </svg>
                                                <div className="business-compare-score-value">
                                                    {item.analysis.overallScore}
                                                </div>
                                            </div>
                                            <div className="business-compare-score-label">
                                                {item.analysis.overallScore >= 80 ? '🟢 Exzellent' :
                                                 item.analysis.overallScore >= 65 ? '🔵 Gut' :
                                                 item.analysis.overallScore >= 50 ? '🟡 Durchschnittlich' :
                                                 '🔴 Ausbaufähig'}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Category Comparison */}
                        <div className="business-compare-section">
                            <h2><i className="fas fa-layer-group"></i> Kategorien-Vergleich</h2>
                            <div className="business-compare-categories">
                                {[
                                    { key: 'brandingScore', label: 'Branding & Markenimage', icon: 'fas fa-palette' },
                                    { key: 'leadScore', label: 'Lead-Quellen-Stärke', icon: 'fas fa-funnel-dollar' },
                                    { key: 'productScore', label: 'Produkt-Portfolio', icon: 'fas fa-box' },
                                    { key: 'proofScore', label: 'Social Proof', icon: 'fas fa-certificate' }
                                ].map(category => {
                                    const bestIndex = getBestPerformer(category.key);
                                    return (
                                        <div key={category.key} className="business-compare-category">
                                            <div className="business-compare-category-header">
                                                <i className={category.icon}></i>
                                                <span>{category.label}</span>
                                            </div>
                                            <div className="business-compare-category-bars">
                                                {analyses.map((item, index) => {
                                                    const score = item.analysis[category.key];
                                                    const isBest = bestIndex === index;
                                                    return (
                                                        <div key={item.id} className="business-compare-bar-row">
                                                            <span className="business-compare-bar-label">
                                                                {item.analysis.name}
                                                                {isBest && <span className="business-compare-bar-crown">👑</span>}
                                                            </span>
                                                            <div className="business-compare-bar">
                                                                <div
                                                                    className="business-compare-bar-fill"
                                                                    style={{
                                                                        width: `${score}%`,
                                                                        background: getScoreColor(score)
                                                                    }}
                                                                ></div>
                                                            </div>
                                                            <span className="business-compare-bar-value">{score}</span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Metrics Comparison */}
                        <div className="business-compare-section">
                            <h2><i className="fas fa-chart-line"></i> Metriken-Vergleich</h2>
                            <div className="business-compare-metrics">
                                <div className="business-compare-metric-row">
                                    <div className="business-compare-metric-label">
                                        <i className="fas fa-funnel-dollar"></i>
                                        Lead-Quellen
                                    </div>
                                    {analyses.map(item => (
                                        <div key={item.id} className="business-compare-metric-value">
                                            {item.analysis.leadCount}
                                        </div>
                                    ))}
                                </div>
                                <div className="business-compare-metric-row">
                                    <div className="business-compare-metric-label">
                                        <i className="fas fa-box"></i>
                                        Produkte
                                    </div>
                                    {analyses.map(item => (
                                        <div key={item.id} className="business-compare-metric-value">
                                            {item.analysis.productCount}
                                        </div>
                                    ))}
                                </div>
                                <div className="business-compare-metric-row">
                                    <div className="business-compare-metric-label">
                                        <i className="fas fa-users"></i>
                                        Gesamt Follower
                                    </div>
                                    {analyses.map(item => (
                                        <div key={item.id} className="business-compare-metric-value">
                                            {formatNumber(item.analysis.totalFollowers)}
                                        </div>
                                    ))}
                                </div>
                                <div className="business-compare-metric-row">
                                    <div className="business-compare-metric-label">
                                        <i className="fas fa-certificate"></i>
                                        Social Proof
                                    </div>
                                    {analyses.map(item => (
                                        <div key={item.id} className="business-compare-metric-value">
                                            {item.analysis.socialProofCount}
                                        </div>
                                    ))}
                                </div>
                                <div className="business-compare-metric-row">
                                    <div className="business-compare-metric-label">
                                        <i className="fas fa-sync"></i>
                                        Recurring Revenue
                                    </div>
                                    {analyses.map(item => (
                                        <div key={item.id} className="business-compare-metric-value">
                                            {item.analysis.hasRecurring ? '✅' : '❌'}
                                        </div>
                                    ))}
                                </div>
                                <div className="business-compare-metric-row">
                                    <div className="business-compare-metric-label">
                                        <i className="fas fa-gem"></i>
                                        High-Ticket (€500+)
                                    </div>
                                    {analyses.map(item => (
                                        <div key={item.id} className="business-compare-metric-value">
                                            {item.analysis.hasHighTicket ? '✅' : '❌'}
                                        </div>
                                    ))}
                                </div>
                                <div className="business-compare-metric-row">
                                    <div className="business-compare-metric-label">
                                        <i className="fas fa-rocket"></i>
                                        Revolutionär
                                    </div>
                                    {analyses.map(item => (
                                        <div key={item.id} className="business-compare-metric-value">
                                            {item.analysis.hasRevolutionary ? '🚀' : '—'}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Winner Analysis */}
                        <div className="business-compare-winner">
                            <h2><i className="fas fa-trophy"></i> Gewinner-Analyse</h2>
                            <div className="business-compare-winner-content">
                                {(() => {
                                    const bestOverall = analyses[getBestPerformer('overallScore')];
                                    const bestBranding = analyses[getBestPerformer('brandingScore')];
                                    const bestLeads = analyses[getBestPerformer('leadScore')];
                                    const bestProducts = analyses[getBestPerformer('productScore')];
                                    
                                    return (
                                        <>
                                            <div className="business-compare-winner-card main">
                                                <i className="fas fa-crown"></i>
                                                <h3>Gesamt-Sieger</h3>
                                                <p className="business-compare-winner-name">{bestOverall.analysis.name}</p>
                                                <p className="business-compare-winner-score">Score: {bestOverall.analysis.overallScore}/100</p>
                                            </div>
                                            <div className="business-compare-winner-grid">
                                                <div className="business-compare-winner-card">
                                                    <i className="fas fa-palette"></i>
                                                    <h4>Bestes Branding</h4>
                                                    <p>{bestBranding.analysis.name}</p>
                                                </div>
                                                <div className="business-compare-winner-card">
                                                    <i className="fas fa-funnel-dollar"></i>
                                                    <h4>Stärkste Lead-Quellen</h4>
                                                    <p>{bestLeads.analysis.name}</p>
                                                </div>
                                                <div className="business-compare-winner-card">
                                                    <i className="fas fa-box"></i>
                                                    <h4>Bestes Produkt-Portfolio</h4>
                                                    <p>{bestProducts.analysis.name}</p>
                                                </div>
                                            </div>
                                        </>
                                    );
                                })()}
                            </div>
                        </div>
                    </>
                ) : selectedIds.length === 0 ? (
                    <div className="business-compare-empty">
                        <i className="fas fa-hand-pointer"></i>
                        <h3>Wähle Analysen zum Vergleichen</h3>
                        <p>Klicke auf mindestens 2 Analysen oben, um einen detaillierten Vergleich zu starten.</p>
                    </div>
                ) : selectedIds.length === 1 ? (
                    <div className="business-compare-empty">
                        <i className="fas fa-balance-scale"></i>
                        <h3>Noch 1 Analyse benötigt</h3>
                        <p>Wähle mindestens eine weitere Analyse aus, um den Vergleich zu starten.</p>
                    </div>
                ) : null}
            </div>
        </div>
    );
}

export default BusinessAnalysisCompare;

